import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Select, DatePicker } from "antd";
import dayjs from "dayjs";
import Utilities from "../../../dependencies/custom/react-utilities";
import domainSettings from "../../../dependencies/custom/settings";
import { useNavigate } from "react-router-dom";

const CATEGORY_OPTIONS = [
  { value: "MDD", label: "Music, Dance & Drama" },
  { value: "Sports", label: "Sports & Physical Education" },
  { value: "Languages", label: "Languages Extension Clubs" },
  { value: "Academic Clubs", label: "Academic Clubs & Societies" },
  { value: "Special Interest", label: "Special Interest Activities" },
];

const LEVEL_OPTIONS = [
  { value: "Internal", label: "Internal" },
  { value: "Inter-class", label: "Inter-class" },
  { value: "Zonal", label: "Zonal" },
  { value: "District", label: "District" },
  { value: "National", label: "National" },
  { value: "Whole School", label: "Whole School" },
];

const CalendarEventFormModal = ({
  visible,
  onCancel,
  onSave,
  loading,
  editRecord = null,
  calendars = [],
  terms = [],
  defaultValues = null,
}) => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [staff, setStaff] = useState([]);
  const watchedCalendarID = Form.useWatch("calendarID", form);

  useEffect(() => {
    if (!visible) {
      form.resetFields();
      return;
    }

    const locked = editRecord?.calendarID || defaultValues?.calendarID;
    const calObj = calendars.find((c) => c.id === locked);
    const termObj = terms.find((t) => t.id === calObj?.termID);
    const tStart = termObj?.start;
    const tEnd = termObj?.end;
    const inRange = (d) => {
      if (!d) return false;
      if (tStart && d.isBefore(dayjs(tStart), "day")) return false;
      if (tEnd && d.isAfter(dayjs(tEnd), "day")) return false;
      return true;
    };

    if (editRecord) {
      form.setFieldsValue({
        calendarID: editRecord.calendarID,
        title: editRecord.title,
        category: editRecord.category,
        eventLevel: editRecord.eventLevel,
        scheduledDate: editRecord.scheduledDate
          ? dayjs(editRecord.scheduledDate)
          : null,
        endDate: editRecord.endDate ? dayjs(editRecord.endDate) : null,
        venue: editRecord.venue,
        description: editRecord.description,
        responsibleTeacherIDs: Array.isArray(editRecord.responsibleTeacherIDs)
          ? editRecord.responsibleTeacherIDs
          : [],
      });
    } else if (defaultValues) {
      const d = defaultValues.scheduledDate
        ? dayjs(defaultValues.scheduledDate)
        : null;
      form.setFieldsValue({
        calendarID: defaultValues.calendarID || undefined,
        scheduledDate: inRange(d) ? d : null,
      });
    }
  }, [visible, editRecord, defaultValues, form, calendars, terms]);

  useEffect(() => {
    if (!visible) return;
    Utilities.fetchWithAuth(
      "GET",
      domainSettings.urls.auth.staffList,
      null,
      navigate,
    )
      .then((res) => {
        if (res.ok)
          setStaff(
            Array.isArray(res.data) ? res.data : res.data?.results || [],
          );
      })
      .catch(() => {});
  }, [visible, navigate]);

  const lockedCalendarID = editRecord?.calendarID || defaultValues?.calendarID;
  const effectiveCalendarID = lockedCalendarID || watchedCalendarID;
  const calObj = calendars.find((c) => c.id === effectiveCalendarID);
  const term = terms.find((t) => t.id === calObj?.termID);
  const termStart = term?.start;
  const termEnd = term?.end;

  const disabledDate = (current) => {
    if (termStart && current.isBefore(dayjs(termStart), "day")) return true;
    if (termEnd && current.isAfter(dayjs(termEnd), "day")) return true;
    return false;
  };

  async function handleOk() {
    const values = await form.validateFields();
    if (lockedCalendarID) values.calendarID = lockedCalendarID;
    if (values.scheduledDate)
      values.scheduledDate = dayjs(values.scheduledDate).format("YYYY-MM-DD");
    if (values.endDate)
      values.endDate = dayjs(values.endDate).format("YYYY-MM-DD");
    else delete values.endDate;
    onSave(values);
  }

  return (
    <Modal
      title={editRecord ? "Edit Event" : "Add Calendar Event"}
      open={visible}
      onCancel={onCancel}
      onOk={handleOk}
      confirmLoading={loading}
      okText={editRecord ? "Save" : "Create"}
      width={560}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        {!lockedCalendarID && (
          <Form.Item
            name="calendarID"
            label="Calendar"
            rules={[{ required: true, message: "Select a calendar" }]}
          >
            <Select
              showSearch
              placeholder="Select calendar"
              optionFilterProp="label"
              options={calendars.map((c) => ({
                value: c.id,
                label: `${c.termName} ${c.academicYear}`,
              }))}
            />
          </Form.Item>
        )}
        <Form.Item
          name="title"
          label="Event Title"
          rules={[{ required: true }]}
        >
          <Input placeholder="e.g. Zonal Ball Games" />
        </Form.Item>
        <Form.Item
          name="category"
          label="Category"
          rules={[{ required: true }]}
        >
          <Select options={CATEGORY_OPTIONS} placeholder="Select category" />
        </Form.Item>
        <Form.Item
          name="eventLevel"
          label="Event Level"
          rules={[{ required: true }]}
        >
          <Select options={LEVEL_OPTIONS} placeholder="Select level" />
        </Form.Item>
        <Form.Item
          name="scheduledDate"
          label="Scheduled Date"
          rules={[{ required: true }]}
        >
          <DatePicker
            style={{ width: "100%" }}
            disabledDate={disabledDate}
            defaultPickerValue={termStart ? dayjs(termStart) : undefined}
          />
        </Form.Item>
        <Form.Item name="endDate" label="End Date (if multi-day)">
          <DatePicker
            style={{ width: "100%" }}
            disabledDate={disabledDate}
            defaultPickerValue={termStart ? dayjs(termStart) : undefined}
          />
        </Form.Item>
        <Form.Item name="venue" label="Venue">
          <Input placeholder="e.g. School Sports Ground" />
        </Form.Item>
        <Form.Item name="description" label="Description">
          <Input.TextArea rows={2} />
        </Form.Item>
        <Form.Item name="responsibleTeacherIDs" label="Responsible Teachers">
          <Select
            mode="multiple"
            showSearch
            allowClear
            placeholder="Select teachers"
            optionFilterProp="label"
            options={staff.map((s) => ({
              value: s.userID,
              label: s.fullName || s.username,
            }))}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CalendarEventFormModal;
